package com.patronat.top.client.data.location

import com.patronat.top.client.location.ClientGeoLocation
import com.patronat.top.client.location.ClientLocationSuggestion
import com.patronat.top.core.network.NetworkResult
import com.patronat.top.core.network.safeApiCall
import io.ktor.client.HttpClient
import io.ktor.client.request.get
import io.ktor.client.request.parameter
import io.ktor.http.encodeURLPathPart
import kotlinx.serialization.SerialName
import kotlinx.serialization.Serializable

class MapTilerLocationRemoteDataSource(
    private val httpClient: HttpClient,
    private val apiKey: String
) {
    suspend fun searchLocations(
        query: String,
        proximity: ClientGeoLocation?,
        limit: Int = 8
    ): NetworkResult<List<ClientLocationSuggestion>> {
        return safeApiCall<MapTilerGeocodingResponse> {
            httpClient.get("https://api.maptiler.com/geocoding/${query.encodeURLPathPart()}.json") {
                parameter("key", apiKey)
                parameter("country", "UA")
                parameter("limit", limit)
                proximity?.let {
                    parameter("proximity", "${it.longitude},${it.latitude}")
                }
            }
        }.mapSuccess { response ->
            response.features.mapNotNull(MapTilerFeature::toLocationSuggestion)
        }
    }

    suspend fun reverseGeocode(location: ClientGeoLocation): NetworkResult<ClientLocationSuggestion?> {
        return safeApiCall<MapTilerGeocodingResponse> {
            httpClient.get("https://api.maptiler.com/geocoding/${location.longitude},${location.latitude}.json") {
                parameter("key", apiKey)
            }
        }.mapSuccess { response ->
            response.features
                .firstOrNull { it.placeType.hasSettlementType() }
                ?.toLocationSuggestion()
                ?: response.features.firstOrNull()?.toLocationSuggestion()
        }
    }

    private inline fun <T, R> NetworkResult<T>.mapSuccess(mapper: (T) -> R): NetworkResult<R> {
        return when (this) {
            is NetworkResult.Success -> NetworkResult.Success(mapper(data))
            is NetworkResult.Error -> this
        }
    }
}

@Serializable
private data class MapTilerGeocodingResponse(
    val features: List<MapTilerFeature> = emptyList()
)

@Serializable
private data class MapTilerFeature(
    val id: String = "",
    val text: String? = null,
    @SerialName("place_name")
    val placeName: String = "",
    @SerialName("place_type")
    val placeType: List<String> = emptyList(),
    val geometry: MapTilerGeometry? = null,
    val context: List<MapTilerContext> = emptyList()
) {
    fun toLocationSuggestion(): ClientLocationSuggestion? {
        val coordinates = geometry?.coordinates ?: return null
        val longitude = coordinates.getOrNull(0) ?: return null
        val latitude = coordinates.getOrNull(1) ?: return null
        val title = settlementName()

        return ClientLocationSuggestion(
            id = id.ifBlank { placeName },
            title = title,
            placeName = placeName.ifBlank { title },
            latitude = latitude,
            longitude = longitude,
            placeType = placeType
        )
    }

    private fun settlementName(): String {
        val contextName = context.firstNotNullOfOrNull { item ->
            item.text?.takeIf { text -> item.id.orEmpty().isSettlementContextId() && text.isNotBlank() }
        }
        return when {
            placeType.hasSettlementType() && !text.isNullOrBlank() -> text
            !contextName.isNullOrBlank() -> contextName
            !text.isNullOrBlank() -> text
            placeName.isNotBlank() -> placeName.substringBefore(",").trim()
            else -> ""
        }
    }
}

@Serializable
private data class MapTilerGeometry(
    val coordinates: List<Double> = emptyList()
)

@Serializable
private data class MapTilerContext(
    val id: String? = null,
    val text: String? = null
)

private fun List<String>.hasSettlementType(): Boolean {
    return any { type ->
        type in setOf("place", "city", "town", "village", "municipality", "locality")
    }
}

private fun String.isSettlementContextId(): Boolean {
    return startsWith("place.") ||
        startsWith("locality.") ||
        startsWith("municipality.") ||
        startsWith("region.")
}
